import { useEffect, useRef, useState } from "react";
import { X, Download, Share2 } from "lucide-react";
import { toast } from "sonner";

const SIZES = { square: [1080, 1080], tall: [1080, 1920] };

const fitFont = (ctx, text, maxW, start, weight) => {
  let size = start;
  do {
    ctx.font = `${weight} ${size}px Outfit, sans-serif`;
    if (ctx.measureText(text).width <= maxW) break;
    size -= 4;
  } while (size > 28);
  return size;
};

const wrap = (ctx, text, maxW) => {
  const words = text.split(" ");
  const lines = [];
  let line = "";
  words.forEach((w) => {
    const t = line ? `${line} ${w}` : w;
    if (ctx.measureText(t).width > maxW && line) { lines.push(line); line = w; } else line = t;
  });
  if (line) lines.push(line);
  return lines.slice(0, 3);
};

const drawCard = (canvas, { name, prize, date, shape }) => {
  const [W, H] = SIZES[shape];
  canvas.width = W; canvas.height = H;
  const ctx = canvas.getContext("2d");
  const tall = shape === "tall";
  ctx.fillStyle = "#060B12"; ctx.fillRect(0, 0, W, H);
  const glow = ctx.createRadialGradient(W / 2, H * 0.45, 40, W / 2, H * 0.45, W * 0.75);
  glow.addColorStop(0, "rgba(37,196,216,0.28)"); glow.addColorStop(1, "rgba(37,196,216,0)");
  ctx.fillStyle = glow; ctx.fillRect(0, 0, W, H);
  ctx.strokeStyle = "rgba(37,196,216,0.12)"; ctx.lineWidth = 2;
  for (let i = 0; i < 9; i++) { ctx.beginPath(); ctx.arc(W * 0.88, H * 0.1, 80 + i * 70, 0, Math.PI * 2); ctx.stroke(); }
  ctx.strokeStyle = "#25C4D8"; ctx.lineWidth = 6; ctx.strokeRect(40, 40, W - 80, H - 80);

  ctx.textAlign = "center"; ctx.textBaseline = "middle";
  const cy = H / 2;
  ctx.fillStyle = "#25C4D8"; ctx.font = "700 40px Outfit, sans-serif";
  ctx.fillText("G I V E A W A Y   W I N N E R", W / 2, cy - (tall ? 330 : 250));
  ctx.fillStyle = "#25C4D8"; ctx.fillRect(W / 2 - 60, cy - (tall ? 280 : 205), 120, 6);

  const size = fitFont(ctx, name, W - 180, tall ? 150 : 130, 800);
  ctx.fillStyle = "#FFFFFF"; ctx.font = `800 ${size}px Outfit, sans-serif`;
  ctx.fillText(name, W / 2, cy - (tall ? 90 : 60));

  if (prize) {
    ctx.fillStyle = "#9FB3BD"; ctx.font = "500 40px Manrope, sans-serif";
    ctx.fillText("wins", W / 2, cy + (tall ? 70 : 60));
    ctx.fillStyle = "#25C4D8"; ctx.font = "700 60px Outfit, sans-serif";
    wrap(ctx, prize, W - 200).forEach((l, i) => ctx.fillText(l, W / 2, cy + (tall ? 160 : 140) + i * 72));
  }

  ctx.fillStyle = "#FFFFFF"; ctx.font = "700 46px Outfit, sans-serif";
  ctx.fillText("THE FISH HOUSE PROJECT", W / 2, H - (tall ? 220 : 160));
  ctx.fillStyle = "#6E8692"; ctx.font = "500 30px Manrope, sans-serif";
  ctx.fillText(`Congratulations!  ·  ${date}`, W / 2, H - (tall ? 160 : 108));
};

export const WinnerCardModal = ({ name, prize, date, onClose }) => {
  const [shape, setShape] = useState("square");
  const ref = useRef(null);
  const dateLabel = new Date(date || Date.now()).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
  const fileName = `winner-${name.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}-${shape}.png`;

  useEffect(() => {
    document.fonts.ready.then(() => ref.current && drawCard(ref.current, { name, prize, date: dateLabel, shape }));
  }, [name, prize, dateLabel, shape]);

  const toBlob = () => new Promise((res) => ref.current.toBlob(res, "image/png"));

  const download = async () => {
    const url = URL.createObjectURL(await toBlob());
    const a = document.createElement("a");
    a.href = url; a.download = fileName; a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  const share = async () => {
    const file = new File([await toBlob()], fileName, { type: "image/png" });
    if (navigator.canShare && navigator.canShare({ files: [file] })) {
      try { await navigator.share({ files: [file], title: "Giveaway winner", text: `Congratulations ${name}!` }); } catch {}
    } else {
      toast.message("Sharing isn't supported here — downloading instead");
      download();
    }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/85 backdrop-blur-sm grid place-items-center p-4 overflow-y-auto" data-testid="winner-card-modal" onClick={onClose}>
      <div className="bg-[#0C131D] border border-white/10 rounded-2xl p-5 w-full max-w-md my-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display font-semibold text-white">Winner card</h3>
          <button onClick={onClose} className="text-brand-textMuted hover:text-white" data-testid="winner-card-close"><X size={20} /></button>
        </div>
        <div className="grid grid-cols-2 gap-2 p-1 bg-[#131C2A] rounded-lg mb-4">
          {[["square", "Square"], ["tall", "Tall (Stories)"]].map(([v, l]) => (
            <button key={v} onClick={() => setShape(v)} data-testid={`winner-card-shape-${v}`}
              className={`py-2 rounded-md text-sm font-medium transition-colors ${shape === v ? "bg-brand-cyan text-brand-base" : "text-brand-textSec"}`}>{l}</button>
          ))}
        </div>
        <div className="grid place-items-center bg-black/40 rounded-lg p-3">
          <canvas ref={ref} className={`rounded-md ${shape === "tall" ? "max-h-[52vh] w-auto" : "w-full h-auto"}`} style={shape === "tall" ? { aspectRatio: "9 / 16" } : undefined} data-testid="winner-card-canvas" />
        </div>
        <div className="flex gap-3 mt-4">
          <button onClick={download} className="btn-primary flex-1 px-4 py-2.5 text-sm inline-flex items-center justify-center gap-2" data-testid="winner-card-download"><Download size={15} /> Download</button>
          <button onClick={share} className="btn-ghost flex-1 px-4 py-2.5 text-sm inline-flex items-center justify-center gap-2" data-testid="winner-card-share"><Share2 size={15} /> Share</button>
        </div>
      </div>
    </div>
  );
};
